import { Helmet } from 'react-helmet-async';
import AdminLayout from '@/components/layout/AdminLayout';
import { useState, useEffect } from 'react';
import apiService from '@/services/api';
import { Download, BarChart3, TrendingUp, Star } from 'lucide-react';

// (audit 2026-06-11) — was a stub page rendering only a heading. Now shows
// per-provider analytics from the existing /admin/analytics/provider-performance
// endpoint, plus an export-to-CSV button.
const AdminProviderAnalyticsPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [rows, setRows] = useState<any[]>([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await apiService.request('/admin/analytics/provider-performance', 'GET');
        if (cancelled) return;
        if (response?.success && Array.isArray(response.data)) {
          setRows(response.data);
        }
      } catch (error) {
        console.error('Error fetching provider analytics:', error);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleExportCSV = () => {
    if (!rows.length) { alert('Nothing to export.'); return; }
    const headers = Object.keys(rows[0]);
    const escape = (v: any) => {
      const s = v == null ? '' : String(v);
      return /[",\n]/.test(s) ? `"£{s.replace(/"/g, '""')}"` : s;
    };
    const csv = [headers.join(','), ...rows.map((r) => headers.map((h) => escape(r[h])).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `provider-analytics-£{new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const totalJobs = rows.reduce((s, r) => s + (Number(r.total_jobs) || 0), 0);
  const completedJobs = rows.reduce((s, r) => s + (Number(r.completed) || 0), 0);
  const avgCompletionRate = rows.length
    ? Math.round(rows.reduce((s, r) => s + (Number(r.completion_rate) || 0), 0) / rows.length)
    : 0;

  return (
    <>
      <Helmet>
        <title>Provider Analytics - Virewire Admin</title>
      </Helmet>
      <AdminLayout>
        <div className="bg-gray-50 min-h-screen p-8">
          <div className="bg-white rounded-xl border border-gray-200 p-8 shadow-sm">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-4">
              <div>
                <h1 className="text-4xl font-bold text-gray-900 mb-2">Provider Analytics</h1>
                <p className="text-gray-600">Detailed analytics and reporting for provider performance metrics.</p>
              </div>
              <button
                onClick={handleExportCSV}
                disabled={isLoading || !rows.length}
                className="bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white font-bold px-4 py-2 rounded-lg flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Export CSV
              </button>
            </div>

            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-center gap-3">
                <BarChart3 className="w-8 h-8 text-blue-600" />
                <div>
                  <p className="text-xs uppercase font-bold text-gray-600">Active Providers</p>
                  <p className="text-2xl font-bold text-blue-700">{rows.length}</p>
                </div>
              </div>
              <div className="bg-teal-50 border border-teal-200 rounded-lg p-4 flex items-center gap-3">
                <TrendingUp className="w-8 h-8 text-teal-600" />
                <div>
                  <p className="text-xs uppercase font-bold text-gray-600">Completed / Total Jobs</p>
                  <p className="text-2xl font-bold text-teal-700">{completedJobs} / {totalJobs}</p>
                </div>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
                <Star className="w-8 h-8 text-green-600" />
                <div>
                  <p className="text-xs uppercase font-bold text-gray-600">Avg Completion Rate</p>
                  <p className="text-2xl font-bold text-green-700">{avgCompletionRate}%</p>
                </div>
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Rank</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-widest">Provider</th>
                    <th className="px-4 py-3 text-right text-xs font-bold text-gray-600 uppercase tracking-widest">Total Jobs</th>
                    <th className="px-4 py-3 text-right text-xs font-bold text-gray-600 uppercase tracking-widest">Completed</th>
                    <th className="px-4 py-3 text-right text-xs font-bold text-gray-600 uppercase tracking-widest">Completion %</th>
                    <th className="px-4 py-3 text-right text-xs font-bold text-gray-600 uppercase tracking-widest">QA Score</th>
                    <th className="px-4 py-3 text-right text-xs font-bold text-gray-600 uppercase tracking-widest">Rating</th>
                  </tr>
                </thead>
                <tbody>
                  {isLoading && (
                    <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">Loading…</td></tr>
                  )}
                  {!isLoading && rows.length === 0 && (
                    <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500">No provider data available.</td></tr>
                  )}
                  {!isLoading && rows.map((r) => (
                    <tr key={r.rank ?? r.provider_name} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm font-bold text-gray-900">#{r.rank ?? '—'}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{r.provider_name || '—'}</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-700">{r.total_jobs ?? 0}</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-700">{r.completed ?? 0}</td>
                      <td className="px-4 py-3 text-sm text-right font-bold text-teal-700">{r.completion_rate ?? 0}%</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-700">{r.qa_score ?? 0}</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-700">{r.customer_rating ?? 0}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </AdminLayout>
    </>
  );
};

export default AdminProviderAnalyticsPage;
